import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { apiClient, ApiError } from '@/lib/apiClient'
import { accessToken } from '@/lib/staffSession'

import { useAdminMutation } from '../api'
import type { ExpenseAttachment } from '../types'

/**
 * Le justificatif d'une dépense.
 *
 * Une dépense sans facture se conteste — et c'est trois mois plus tard, au
 * moment de rendre les comptes, qu'on découvre laquelle manque. Le geste doit
 * donc se faire **au moment où on saisit**, pas dans un second écran qu'on
 * n'ouvrira jamais.
 *
 * `capture="environment"` ouvre l'appareil photo arrière sur un téléphone : on
 * photographie le ticket en sortant du magasin, on ne le rapporte pas pour le
 * scanner le soir. Sur un ordinateur, l'attribut est ignoré et le champ
 * redevient un sélecteur de fichier ordinaire.
 *
 * Les PDF sont acceptés autant que les images : un fournisseur envoie une
 * facture par e-mail, un commerçant donne un ticket de caisse.
 *
 * Retirer un justificatif ne supprime pas le fichier : il reste dans la
 * médiathèque, où il sert peut-être ailleurs. C'est le rattachement qu'on
 * défait.
 */
export function ReceiptField({
  eventId,
  expenseId,
  attachments,
  onChanged,
}: {
  eventId: string | undefined
  expenseId: string
  attachments: ExpenseAttachment[]
  onChanged: () => void
}) {
  const { t } = useTranslation()
  const input = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const base = `/admin/events/${eventId}/finance/expenses/${expenseId}`

  const detach = useAdminMutation<{ message: string }, { id: string }>(
    'delete',
    (variables) => `${base}/attachments/${variables.id}`,
    eventId,
    [['admin-expenses'], ['admin-spenders', eventId ?? '']],
  )

  async function upload(file: File) {
    setBusy(true)
    setError(null)
    try {
      const body = new FormData()
      body.append('file', file)
      const token = await accessToken()
      const asset = await apiClient.post<{ id: string }>('/admin/media', body, {
        token,
        ...(eventId ? { eventId } : {}),
      })
      await apiClient.post(
        `${base}/attachments`,
        { media_id: asset.id, label: file.name },
        { token, ...(eventId ? { eventId } : {}) },
      )
      onChanged()
    } catch (caught) {
      setError(
        caught instanceof ApiError || caught instanceof Error
          ? caught.message
          : t('admin.finance.receiptFailed'),
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <p className="text-caption uppercase tracking-wide text-content-muted">
        {t('admin.finance.receipts')}
      </p>

      {attachments.length > 0 ? (
        <ul className="mt-2 grid gap-1.5">
          {attachments.map((attachment) => (
            <li
              key={attachment.id}
              className="flex items-center gap-3 rounded-md border border-line-subtle px-3 py-2"
            >
              <span className="min-w-0 flex-1 truncate text-sm text-content-primary">
                {attachment.label ?? t('admin.finance.receipt')}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={detach.isPending}
                onClick={() =>
                  detach.mutate({ id: attachment.id }, { onSuccess: onChanged })
                }
              >
                {t('admin.finance.receiptRemove')}
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-2 text-xs text-content-muted">
          {t('admin.finance.receiptsEmpty')}
        </p>
      )}

      <input
        ref={input}
        type="file"
        // La facture arrive en photo ou en PDF, jamais dans un format choisi
        // par nous : on accepte les deux.
        accept="image/*,application/pdf"
        capture="environment"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0]
          // Remis à zéro : sans cela, reprendre le même fichier après un échec
          // ne déclencherait aucun évènement.
          event.target.value = ''
          if (file) void upload(file)
        }}
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="mt-2"
        disabled={busy}
        onClick={() => input.current?.click()}
      >
        {busy ? t('admin.finance.receiptSending') : t('admin.finance.receiptAdd')}
      </Button>

      {error ? <p className="mt-2 text-sm text-state-danger">{error}</p> : null}
      {detach.error ? (
        <p className="mt-2 text-sm text-state-danger">{detach.error.message}</p>
      ) : null}
    </div>
  )
}
